import React, { useState } from 'react';
import Button from '../../../Components/Button/Button';
import { postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { validatePassword } from '../../../Utils/validationHelper';

function getAuthToken() {
  try {
    const tokenItem = localStorage.getItem('token');
    if (!tokenItem) return '';
    try {
      return JSON.parse(tokenItem);
    } catch {
      return tokenItem;
    }
  } catch {
    return '';
  }
}

export default function ChangePassword() {
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name, value, formData) => {
    const errors = { ...passwordErrors };

    switch (name) {
      case 'oldPassword':
        if (!value) {
          errors.oldPassword = 'Old password is required';
        } else {
          delete errors.oldPassword;
        }
        if (formData.newPassword) {
          if (formData.newPassword === value) {
            errors.newPassword = 'New password must be different from old password';
          } else {
            const passwordError = validatePassword(formData.newPassword);
            if (passwordError) {
              errors.newPassword = passwordError;
            } else {
              delete errors.newPassword;
            }
          }
        }
        break;

      case 'newPassword':
        if (!value) {
          errors.newPassword = 'New password is required';
        } else if (formData.oldPassword && value === formData.oldPassword) {
          errors.newPassword = 'New password must be different from old password';
        } else {
          const passwordError = validatePassword(value);
          if (passwordError) {
            errors.newPassword = passwordError;
          } else {
            delete errors.newPassword;
          }
        }
        if (formData.confirmPassword) {
          if (formData.confirmPassword !== value) {
            errors.confirmPassword = 'Passwords do not match';
          } else {
            delete errors.confirmPassword;
          }
        }
        break;

      case 'confirmPassword':
        if (!value) {
          errors.confirmPassword = 'Please confirm your new password';
        } else if (formData.newPassword && value !== formData.newPassword) {
          errors.confirmPassword = 'Passwords do not match';
        } else {
          delete errors.confirmPassword;
        }
        break;

      default:
        break;
    }

    setPasswordErrors(errors);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...passwordData, [name]: value };
    setPasswordData(updatedData);
    validateField(name, value, updatedData);
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.oldPassword) {
      errors.oldPassword = 'Old password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.oldPassword && passwordData.newPassword === passwordData.oldPassword) {
      errors.newPassword = 'New password must be different from old password';
    } else {
      const passwordError = validatePassword(passwordData.newPassword);
      if (passwordError) {
        errors.newPassword = passwordError;
      }
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword && passwordData.confirmPassword !== passwordData.newPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Session expired. Please sign in again.');
        return;
      }

      const payload = {
        token,
        password: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      };

      const response = await postApi('/admin/reset-password', payload);

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success(response?.message || 'Password changed successfully');
        setPasswordData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setPasswordErrors({});
      } else {
        toast.error(response?.message || 'Failed to change password. Please try again.');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to change password. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = (fieldName) => {
    setShowPasswords((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  return (
    <div className="settings-password-card">
      <form onSubmit={handleChangePassword} className="password-form">
        <div className="password-form-row">
          <div className="password-form-column">
            <label htmlFor="oldPassword" className="input-label">
              Old Password
              <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                id="oldPassword"
                name="oldPassword"
                type={showPasswords.oldPassword ? 'text' : 'password'}
                value={passwordData.oldPassword}
                onChange={handlePasswordChange}
                className={`input-field ${passwordErrors.oldPassword ? 'input-error' : ''}`.trim()}
                required
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => togglePasswordVisibility('oldPassword')}
              >
                <img
                  src={window.getPublicUrl(
                    showPasswords.oldPassword ? '/assets/images/Eye.svg' : '/assets/images/eye-closed.svg'
                  )}
                  alt={showPasswords.oldPassword ? 'Hide password' : 'Show password'}
                  className="password-eye-icon"
                />
              </button>
            </div>
            <div className={`error-message-container ${passwordErrors.oldPassword ? 'has-error' : ''}`}>
              {passwordErrors.oldPassword && <span className="error-message">{passwordErrors.oldPassword}</span>}
            </div>
          </div>
        </div>
        <div className="password-form-row">
          <div className="password-form-column">
            <label htmlFor="newPassword" className="input-label">
              New Password
              <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                id="newPassword"
                name="newPassword"
                type={showPasswords.newPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className={`input-field ${passwordErrors.newPassword ? 'input-error' : ''}`.trim()}
                required
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => togglePasswordVisibility('newPassword')}
              >
                <img
                  src={window.getPublicUrl(
                    showPasswords.newPassword ? '/assets/images/Eye.svg' : '/assets/images/eye-closed.svg'
                  )}
                  alt={showPasswords.newPassword ? 'Hide password' : 'Show password'}
                  className="password-eye-icon"
                />
              </button>
            </div>
            <div className={`error-message-container ${passwordErrors.newPassword ? 'has-error' : ''}`}>
              {passwordErrors.newPassword && <span className="error-message">{passwordErrors.newPassword}</span>}
            </div>
          </div>
        </div>
        <div className="password-form-row">
          <div className="password-form-column">
            <label htmlFor="confirmPassword" className="input-label">
              Confirm Password
              <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className={`input-field ${passwordErrors.confirmPassword ? 'input-error' : ''}`.trim()}
                required
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => togglePasswordVisibility('confirmPassword')}
              >
                <img
                  src={window.getPublicUrl(
                    showPasswords.confirmPassword ? '/assets/images/Eye.svg' : '/assets/images/eye-closed.svg'
                  )}
                  alt={showPasswords.confirmPassword ? 'Hide password' : 'Show password'}
                  className="password-eye-icon"
                />
              </button>
            </div>
            <div className={`error-message-container ${passwordErrors.confirmPassword ? 'has-error' : ''}`}>
              {passwordErrors.confirmPassword && <span className="error-message">{passwordErrors.confirmPassword}</span>}
            </div>
          </div>
        </div>
        <div className="password-form-actions">
          <Button
            type="submit"
            className="btn-success password-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </form>
    </div>
  );
}
